import { useRef, useEffect } from 'react'
import { Tooltip } from '@mui/material'
import { useModal } from '@app/helpers'
import { faUniversalAccess } from '@fortawesome/pro-solid-svg-icons/faUniversalAccess'
import { AccessibilityMenuItem } from './components'
import { AccessibilityMenuWrapper, MenuContent, StyledIcon, StyledIconButton } from './styles'
import { AccessibilityTestMenuProps } from './types'
import TextResize from '@app/components/common/TextResize'

const AccessibilityTestMenu: React.FC<AccessibilityTestMenuProps> = ({
  fontSizeControl,
  readAloudControl,
  answerMaskingControl,
  screenMaskingControl,
  isReviewMode = false,
}) => {
  const { open, onClose, onOpen } = useModal()
  const anchorEl = useRef<HTMLButtonElement>(null)

  // Force popover position update when text size changes
  useEffect(() => {
    if (open) {
      const observer = new MutationObserver(() => {
        window.dispatchEvent(new Event('resize'))
      })

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['style'],
      })

      return () => observer.disconnect()
    }
  }, [open])

  return (
    <>
      <Tooltip title='Accessibility Tools'>
        <StyledIconButton
          onClick={onOpen}
          className={open ? 'active' : ''}
          ref={anchorEl}
          aria-label='Open accessibility menu'
          aria-expanded={open}
          aria-controls='accessibility-testing-menu'
          data-automation-id='accessibility-testing-menu-button'
        >
          <StyledIcon icon={faUniversalAccess} aria-hidden='true' />
        </StyledIconButton>
      </Tooltip>

      <AccessibilityMenuWrapper
        id='accessibility-testing-menu'
        open={open}
        onClose={onClose}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        role='dialog'
        aria-label='Accessibility options'
      >
        <MenuContent>
          {fontSizeControl && <TextResize {...fontSizeControl} />}

          {!isReviewMode && (
            <>
              {readAloudControl && (
                <AccessibilityMenuItem
                  label='Read Aloud'
                  checked={readAloudControl.checked}
                  onChange={readAloudControl.toggle}
                  dataAutomationId='read-aloud-switch'
                />
              )}

              {answerMaskingControl?.featureFlagEnabled && (
                <AccessibilityMenuItem
                  label='Answer Masking'
                  checked={answerMaskingControl.checked}
                  onChange={answerMaskingControl.toggle}
                  dataAutomationId='answer-masking-switch'
                />
              )}

              {screenMaskingControl?.featureFlagEnabled && (
                <AccessibilityMenuItem
                  label='Screen Masking'
                  checked={screenMaskingControl.checked}
                  onChange={screenMaskingControl.toggle}
                  dataAutomationId='screen-masking-switch'
                />
              )}
            </>
          )}
        </MenuContent>
      </AccessibilityMenuWrapper>
    </>
  )
}

export default AccessibilityTestMenu
